import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import { useAppTheme } from "@hooks/useAppTheme";
import { Box, CardContainer, MarkdownRenderer } from "@memorang/ui";
import { useTranslation } from "react-i18next";
import type { StyleProp, ViewStyle } from "react-native";
import { Button, Text } from "react-native-paper";

type IconName = keyof typeof Icon.glyphMap;

export type OutlineSection = { title: string; items: string[] };

const capitalizeFirstLetter = (text: string) =>
	text.length ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : text;

const PanelCard = ({
	icon,
	title,
	subtitle,
	headerRight,
	style,
	children,
}: {
	icon: IconName;
	title: string;
	subtitle: string;
	headerRight?: React.ReactNode;
	style?: StyleProp<ViewStyle>;
	children: React.ReactNode;
}) => (
	<CardContainer
		title={title}
		subtitle={subtitle}
		icon={icon}
		headerRight={headerRight}
		style={style}
	>
		<Box paddingHorizontal={16} paddingVertical={8}>
			{children}
		</Box>
	</CardContainer>
);

const LearnedRow = ({
	index,
	text,
	last,
}: {
	index: number;
	text: string;
	last: boolean;
}) => {
	const theme = useAppTheme();
	return (
		<Box
			flexDirection="row"
			alignItems="flex-start"
			paddingVertical={12}
			borderBottomWidth={last ? 0 : 1}
			borderBottomColor={theme.colors.outlineVariant}
			style={{ gap: 14 }}
		>
			<Box
				width={28}
				height={28}
				borderRadius={14}
				backgroundColor={theme.colors.elevation.level3}
				alignItems="center"
				justifyContent="center"
				style={{ marginTop: 1 }}
			>
				<Text
					variant="labelMedium"
					style={{
						color: theme.colors.onPrimaryContainer,
						fontWeight: "800",
					}}
				>
					{index}
				</Text>
			</Box>
			<Box flex={1}>
				<MarkdownRenderer
					text={capitalizeFirstLetter(text)}
					fontSize={14}
					paragraphMarginVertical={0}
				/>
			</Box>
		</Box>
	);
};

const CoveredRow = ({
	icon,
	title,
	last,
}: {
	icon: IconName;
	title: string;
	last: boolean;
}) => {
	const theme = useAppTheme();
	return (
		<Box
			flexDirection="row"
			alignItems="center"
			paddingVertical={14}
			borderBottomWidth={last ? 0 : 1}
			borderBottomColor={theme.colors.outlineVariant}
			style={{ gap: 14 }}
		>
			<Icon
				name={icon}
				size={20}
				color={theme.colors.onSecondaryContainer}
				accessible={false}
			/>
			<Text
				variant="bodyMedium"
				style={{ color: theme.colors.onSurface, flex: 1 }}
			>
				{title}
			</Text>
		</Box>
	);
};

export const LearnedPanel = ({
	objectives,
	style,
}: {
	objectives: string[];
	style?: StyleProp<ViewStyle>;
}) => {
	const { t } = useTranslation();
	if (objectives.length === 0) {
		return null;
	}
	return (
		<PanelCard
			icon="bullseye-arrow"
			title={t("report.proethica.learned.title")}
			subtitle={t("report.proethica.learned.subtitle")}
			style={style}
		>
			{objectives.map((objective, index) => (
				<LearnedRow
					key={objective}
					index={index + 1}
					text={objective}
					last={index === objectives.length - 1}
				/>
			))}
		</PanelCard>
	);
};

// Review sits in this panel's header rather than the footer: it revisits exactly
// the lessons and scenarios listed below it.
export const CoveredPanel = ({
	sections,
	isPreparingReview,
	handleReviewModule,
	style,
}: {
	sections: OutlineSection[];
	isPreparingReview: boolean;
	handleReviewModule: () => void;
	style?: StyleProp<ViewStyle>;
}) => {
	const { t } = useTranslation();
	if (sections.length === 0) {
		return null;
	}
	return (
		<PanelCard
			icon="format-list-checks"
			title={t("report.proethica.covered.title")}
			subtitle={t("report.proethica.covered.subtitle")}
			style={style}
			headerRight={
				<Button
					mode="text"
					compact
					loading={isPreparingReview}
					disabled={isPreparingReview}
					onPress={handleReviewModule}
					style={{ marginRight: 8 }}
				>
					{t("report.proethica.covered.review")}
				</Button>
			}
		>
			{sections.map((section, index) => (
				<CoveredRow
					key={section.title}
					icon={section.items.length > 0 ? "account-outline" : "scale-balance"}
					title={section.title}
					last={index === sections.length - 1}
				/>
			))}
		</PanelCard>
	);
};
